import { Separator } from "@/components/ui/separator";

interface AgencyContractProps {
  lang: 'en' | 'ar';
  data: {
    companyName: string;
    crNumber: string;
    vatNumber: string;
    email: string;
    personInChargeName: string;
    personInChargeTitle: string;
    city: string;
  };
  /** Stable date used in the contract header. Defaults to now. */
  documentDate?: Date;
}

const FUSION = {
  name_en: "Fusion Media for Advertising",
  name_ar: "فيوجن ميديا للإعلان",
  cr: "1009193562",
  email: "mohamed@fusion-media.sa",
  rep_en: "Mohamed Fahad Al-Maliki",
  rep_ar: "محمد فهد المالكي",
  title_en: "Chief Executive Officer",
  title_ar: "الرئيس التنفيذي",
  iban: "SA1180000611608016301369",
};

export const AgencyContract = ({ lang, data, documentDate }: AgencyContractProps) => {
  const today = documentDate ?? new Date();
  const dateStr = today.toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-GB');
  const isAr = lang === 'ar';

  if (isAr) {
    return (
      <div dir="rtl" className="space-y-5 text-sm leading-7 text-foreground">
        <div className="text-center space-y-1">
          <h2 className="text-lg font-bold">عقد وساطة إعلانية</h2>
          <p className="text-muted-foreground">حُرر في مدينة {data.city || "الرياض"} بتاريخ {dateStr}</p>
        </div>

        <p>إنه في يوم {dateStr} تم بعون الله وتوفيقه الاتفاق بين كل من:</p>

        <div className="rounded-lg border border-border p-3 bg-muted/30">
          <p className="font-semibold mb-1">الطرف الأول: {FUSION.name_ar}</p>
          <p>سجل تجاري رقم: {FUSION.cr}</p>
          <p>يمثلها: {FUSION.rep_ar} — {FUSION.title_ar}</p>
          <p>البريد الإلكتروني: {FUSION.email}</p>
          <p className="text-muted-foreground">(ويُشار إليه فيما بعد بـ "الطرف الأول" أو "المنصة")</p>
        </div>

        <div className="rounded-lg border border-border p-3 bg-muted/30">
          <p className="font-semibold mb-1">الطرف الثاني: {data.companyName || "—"}</p>
          <p>سجل تجاري رقم: {data.crNumber || "—"}</p>
          {data.vatNumber && <p>الرقم الضريبي: {data.vatNumber}</p>}
          <p>يمثلها: {data.personInChargeName || "—"} — {data.personInChargeTitle || "—"}</p>
          <p>البريد الإلكتروني: {data.email}</p>
          <p className="text-muted-foreground">(ويُشار إليه فيما بعد بـ "الطرف الثاني" أو "الوسيط")</p>
        </div>

        <p>بعد أن أقر الطرفان بأهليتهما المعتبرة شرعاً ونظاماً للتعاقد، اتفقا على ما يلي:</p>

        <div className="space-y-3">
          <Clause n="1" title="التمهيد">
            يعتبر التمهيد أعلاه جزءاً لا يتجزأ من هذا العقد ومكملاً لأحكامه.
          </Clause>
          <Clause n="2" title="موضوع العقد">
            يلتزم الطرف الثاني بصفته وسيطاً إعلانياً بتسويق وعرض الخدمات والمنتجات الإعلانية الخاصة بالطرف الأول (شاشات DOOH، مساحات إعلانية، باقات المؤثرين، الفعاليات وغيرها) على عملائه، وتحويل العملاء المهتمين إلى المنصة لإتمام الحجز والدفع.
          </Clause>
          <Clause n="3" title="التزامات الطرف الأول">
            <ul className="list-disc pr-5 space-y-1">
              <li>توفير لوحة تحكم مخصصة للطرف الثاني لإدارة العملاء وتتبع العمولات.</li>
              <li>توفير المعلومات والأسعار المحدثة للخدمات والباقات.</li>
              <li>تقديم الدعم الفني والتجاري للطرف الثاني خلال ساعات العمل.</li>
              <li>صرف العمولات المستحقة للطرف الثاني وفق الجدول المعلن في المنصة.</li>
            </ul>
          </Clause>
          <Clause n="4" title="التزامات الطرف الثاني">
            <ul className="list-disc pr-5 space-y-1">
              <li>تمثيل المنصة بصورة احترافية وعدم تقديم وعود أو ضمانات خارج ما تعتمده المنصة رسمياً.</li>
              <li>الالتزام بأنظمة الإعلان في المملكة العربية السعودية وعدم الترويج لمحتوى مخالف للشريعة الإسلامية أو الأنظمة المعمول بها.</li>
              <li>المحافظة على سرية معلومات العملاء والمنصة.</li>
              <li>عدم التواصل المباشر مع مالكي الشاشات أو المؤثرين بشأن طلبات العملاء، وأن تتم جميع المعاملات عبر المنصة.</li>
            </ul>
          </Clause>
          <Clause n="5" title="العمولات والمدفوعات">
            <ul className="list-disc pr-5 space-y-1">
              <li>يستحق الطرف الثاني عمولة عن كل صفقة مكتملة يتم تحويلها عبر حسابه على المنصة، وفق نسب العمولة المعلنة في المنصة.</li>
              <li>تُصرف العمولات خلال خمسة (5) أيام عمل من استلام المنصة لكامل قيمة الصفقة من العميل.</li>
              <li>يتم التحويل إلى حساب الطرف الثاني المسجل في المنصة عبر الآيبان المعتمد.</li>
              <li>آيبان الطرف الأول لاسترجاع الأموال (إن لزم): {FUSION.iban}</li>
            </ul>
          </Clause>
          <Clause n="6" title="السرية وعدم المنافسة">
            يلتزم الطرف الثاني بالحفاظ على سرية جميع المعلومات التجارية والتقنية والمالية التي يطلع عليها بحكم هذا العقد، ويستمر هذا الالتزام لمدة سنتين بعد انتهاء العلاقة التعاقدية.
          </Clause>
          <Clause n="7" title="مدة العقد وإنهاؤه">
            <ul className="list-disc pr-5 space-y-1">
              <li>تبدأ مدة هذا العقد من تاريخ توقيعه ويستمر لمدة سنة قابلة للتجديد تلقائياً ما لم يخطر أحد الطرفين الآخر برغبته في عدم التجديد قبل ثلاثين (30) يوماً من نهاية المدة.</li>
              <li>يحق لأي طرف إنهاء العقد بإشعار كتابي مدته خمسة عشر (15) يوماً مع احتفاظ الطرف الثاني بحقه في العمولات المستحقة عن الصفقات المكتملة قبل تاريخ الإنهاء.</li>
            </ul>
          </Clause>
          <Clause n="8" title="القانون الواجب التطبيق وفض النزاعات">
            يخضع هذا العقد لأنظمة المملكة العربية السعودية. وفي حال نشوء أي نزاع، يسعى الطرفان لتسويته ودياً، وإلا أُحيل إلى المحاكم المختصة في مدينة الرياض.
          </Clause>
        </div>

        <Separator />

        <p className="text-center text-muted-foreground text-xs">
          حُرر هذا العقد إلكترونياً ووُقع رقمياً، ويعتبر التوقيع والختم الرقميان حجة قانونية على ما ورد فيه.
        </p>
      </div>
    );
  }

  // English version
  return (
    <div dir="ltr" className="space-y-5 text-sm leading-7 text-foreground">
      <div className="text-center space-y-1">
        <h2 className="text-lg font-bold">Advertising Brokerage Agreement</h2>
        <p className="text-muted-foreground">Executed in {data.city || "Riyadh"} on {dateStr}</p>
      </div>

      <p>On this day, {dateStr}, by the grace of God, this agreement has been concluded between:</p>

      <div className="rounded-lg border border-border p-3 bg-muted/30">
        <p className="font-semibold mb-1">First Party: {FUSION.name_en}</p>
        <p>Commercial Registration No.: {FUSION.cr}</p>
        <p>Represented by: {FUSION.rep_en} — {FUSION.title_en}</p>
        <p>Email: {FUSION.email}</p>
        <p className="text-muted-foreground">(hereinafter referred to as the "First Party" or the "Platform")</p>
      </div>

      <div className="rounded-lg border border-border p-3 bg-muted/30">
        <p className="font-semibold mb-1">Second Party: {data.companyName || "—"}</p>
        <p>Commercial Registration No.: {data.crNumber || "—"}</p>
        {data.vatNumber && <p>VAT Number: {data.vatNumber}</p>}
        <p>Represented by: {data.personInChargeName || "—"} — {data.personInChargeTitle || "—"}</p>
        <p>Email: {data.email}</p>
        <p className="text-muted-foreground">(hereinafter referred to as the "Second Party" or the "Broker")</p>
      </div>

      <p>Both parties, having confirmed their legal capacity to contract, have agreed to the following:</p>

      <div className="space-y-3">
        <Clause n="1" title="Preamble">
          The above preamble forms an integral and complementary part of this agreement.
        </Clause>
        <Clause n="2" title="Scope of Agreement">
          The Second Party undertakes, in its capacity as an advertising broker, to market and present the First Party's advertising services and products (DOOH screens, ad inventory, influencer packages, events and others) to its clients, and to refer interested clients to the Platform to complete the booking and payment.
        </Clause>
        <Clause n="3" title="Obligations of the First Party">
          <ul className="list-disc pl-5 space-y-1">
            <li>Provide the Second Party with a dedicated dashboard to manage clients and track commissions.</li>
            <li>Provide up-to-date information and pricing of services and packages.</li>
            <li>Provide technical and commercial support during business hours.</li>
            <li>Pay the Second Party the commissions due according to the schedule published on the Platform.</li>
          </ul>
        </Clause>
        <Clause n="4" title="Obligations of the Second Party">
          <ul className="list-disc pl-5 space-y-1">
            <li>Represent the Platform professionally and refrain from offering promises or guarantees beyond those officially adopted by the Platform.</li>
            <li>Comply with the advertising regulations in Saudi Arabia and refrain from promoting content contrary to Islamic Sharia or applicable laws.</li>
            <li>Maintain the confidentiality of client and Platform information.</li>
            <li>Refrain from contacting screen owners or influencers directly regarding client requests; all transactions must occur through the Platform.</li>
          </ul>
        </Clause>
        <Clause n="5" title="Commissions and Payments">
          <ul className="list-disc pl-5 space-y-1">
            <li>The Second Party is entitled to a commission for every completed deal channeled through its Platform account, in accordance with the platform's published commission terms.</li>
            <li>Commissions are paid within five (5) business days from the Platform's receipt of the full deal value from the client.</li>
            <li>Payments are transferred to the Second Party's registered bank account via the approved IBAN.</li>
            <li>First Party IBAN for refunds (if needed): {FUSION.iban}</li>
          </ul>
        </Clause>
        <Clause n="6" title="Confidentiality and Non-Compete">
          The Second Party undertakes to keep confidential all commercial, technical and financial information accessed under this agreement, for two (2) years after the end of the contractual relationship.
        </Clause>
        <Clause n="7" title="Term and Termination">
          <ul className="list-disc pl-5 space-y-1">
            <li>This agreement starts from its signing date for a period of one year, automatically renewed unless either party notifies the other of its intent not to renew at least thirty (30) days before the term ends.</li>
            <li>Either party may terminate the agreement with fifteen (15) days' written notice. The Second Party retains the right to commissions due on deals completed before the termination date.</li>
          </ul>
        </Clause>
        <Clause n="8" title="Governing Law and Disputes">
          This agreement is governed by the laws of the Kingdom of Saudi Arabia. In case of any dispute, the parties shall first seek an amicable settlement; failing that, the matter shall be referred to the competent courts in Riyadh.
        </Clause>
      </div>

      <Separator />

      <p className="text-center text-muted-foreground text-xs">
        This contract has been executed and signed electronically. The digital signature and stamp constitute legal proof of its contents.
      </p>
    </div>
  );
};

const Clause = ({ n, title, children }: { n: string; title: string; children: React.ReactNode }) => (
  <div>
    <p className="font-semibold mb-1">Clause {n}: {title}</p>
    <div className="text-muted-foreground">{children}</div>
  </div>
);


export default AgencyContract;
